import { DatePipe, DecimalPipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { CloseSessionStatsDialogData } from '@posModels';
import { PosTabType } from '@posTypes';
import { SharedModule } from '@shared';

@Component({
  selector: 'app-close-session-stats-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    TranslateModule,
    SharedModule,
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
    SlicePipe,
  ],
  templateUrl: './close-session-stats-dialog.component.html',
  styleUrl: './close-session-stats-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CloseSessionStatsDialogComponent {
  private dialogRef = inject(MatDialogRef<CloseSessionStatsDialogComponent>);
  data = inject<CloseSessionStatsDialogData>(MAT_DIALOG_DATA);

  readonly PosTabType = PosTabType;
  readonly Math = Math;

  get response() {
    return this.data.response;
  }

  get stats() {
    return this.data.response.stats;
  }

  get session() {
    return this.data.response.session;
  }

  get openTabs() {
    return this.data.response.openTabs;
  }

  get openTabsCount() {
    return this.data.response.openTabsCount;
  }

  get currentSessionTabs() {
    return this.openTabs.filter(tab => tab.sessionId === this.session.id);
  }

  get priorSessionTabs() {
    return this.openTabs.filter(tab => tab.sessionId !== this.session.id);
  }

  get currentSessionTabsBalanceCents() {
    return this.currentSessionTabs.reduce((sum, tab) => sum + tab.balanceCents, 0);
  }

  get priorSessionTabsBalanceCents() {
    return this.priorSessionTabs.reduce((sum, tab) => sum + tab.balanceCents, 0);
  }

  get cashReconciliation() {
    return this.data.response.cashReconciliation;
  }

  get tabPaymentBreakdown() {
    return this.data.response.tabPaymentBreakdown;
  }

  get hasTabPaymentBreakdown() {
    return this.tabPaymentBreakdown.totalPaidCents > 0;
  }

  get overheadItemsSorted() {
    return [ ...this.data.response.overheadItems ].sort((a, b) => {
      const bTime = new Date(b.postedAt).getTime();
      const aTime = new Date(a.postedAt).getTime();
      return bTime - aTime;
    });
  }

  get writeOffItems() {
    return this.data.response.writeOffItems ?? [];
  }

  get writeOffTotalCents() {
    return this.writeOffItems.reduce((sum, item) => sum + item.amountCents, 0);
  }

  close(): void {
    this.dialogRef.close();
  }
}
